import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import Button from '../primitives/Button';
import { Select, TextField } from '../primitives/Field';
import {
  BodyText,
  DisplayTitle,
  Eyebrow,
  MutedText,
  SectionLead,
} from '../primitives/Typography';
import PlanPicker from './PlanPicker';
import screeningClient, { ApiError } from 'screening/apiClient';
import {
  updateStoredEmployer,
  getStoredEmployer,
} from '@hooks/useScreeningAuth';
import {
  BillingInterval,
  BillingRegion,
  PlanId,
  ScreeningBilling,
} from 'screening/types';
import {
  COUNTRY_OPTIONS,
  NEVER_PAY_FOR,
  RegionCatalog,
  billingHintForCountry,
  catalogForRegion,
  isBillingReady,
  trialCopy,
} from 'screening/billing/catalog';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 80;
  background: var(--screening-canvas);
  overflow-y: auto;
  padding: 48px 24px 80px;
`;

const Shell = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const Head = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 640px;
`;

const StartSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 640px;
  padding-top: 8px;
`;

const StartCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;

const FormError = styled.p`
  margin: 0;
  font-size: 13px;
  color: var(--screening-reject);
`;

const CountryBlock = styled.div`
  max-width: 360px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EnterpriseSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 640px;
  padding-top: 8px;
  border-top: 1px solid var(--screening-hairline);
`;

const EnterpriseRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
`;

type Props = {
  employerId: string;
  billingRegion: BillingRegion | null | undefined;
  billing: ScreeningBilling | null | undefined;
  onBillingReady: (
    billing: ScreeningBilling,
    extras?: Partial<{
      country_code: string | null;
      billing_region: BillingRegion | null;
    }>
  ) => void;
};

const PlansGate = ({ billingRegion, onBillingReady }: Props) => {
  const router = useRouter();
  const [region, setRegion] = useState<BillingRegion | null>(
    billingRegion || null
  );
  const [countryCode, setCountryCode] = useState('');
  const [interval, setInterval] = useState<BillingInterval>('monthly');
  const [selected, setSelected] = useState<PlanId>('solo');
  const [enterpriseCode, setEnterpriseCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const catalog: RegionCatalog | null = useMemo(
    () => (region ? catalogForRegion(region) : null),
    [region]
  );

  useEffect(() => {
    if (billingRegion) setRegion(billingRegion);
  }, [billingRegion]);

  // After Stripe / Paystack redirect, confirm or refresh me.
  useEffect(() => {
    if (!router.isReady) return;
    if (router.query.billing !== 'success') return;
    let cancelled = false;
    (async () => {
      try {
        const reference =
          typeof router.query.reference === 'string'
            ? router.query.reference
            : typeof router.query.trxref === 'string'
            ? router.query.trxref
            : null;
        const sessionId =
          typeof router.query.session_id === 'string'
            ? router.query.session_id
            : null;

        if (reference || sessionId) {
          const res = await screeningClient.billingConfirm(
            sessionId ? { session_id: sessionId } : { reference: reference! }
          );
          if (cancelled) return;
          const employer = getStoredEmployer();
          if (employer) {
            updateStoredEmployer({
              ...employer,
              country_code: res.country_code,
              billing_region: res.billing_region as BillingRegion,
              billing: res.billing,
            });
          }
          onBillingReady(res.billing, {
            country_code: res.country_code,
            billing_region: res.billing_region as BillingRegion,
          });
          toast.success('Trial started.');
        } else {
          const me = await screeningClient.me();
          if (cancelled) return;
          updateStoredEmployer(me.employer);
          if (
            me.employer.billing &&
            isBillingReady(
              me.employer.billing.status,
              me.employer.billing.plan_id
            )
          ) {
            onBillingReady(me.employer.billing, {
              country_code: me.employer.country_code,
              billing_region: me.employer.billing_region,
            });
          }
        }
        router.replace('/screening', undefined, { shallow: true });
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof ApiError
              ? err.message
              : 'Payment returned but trial could not be confirmed.'
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    router.isReady,
    router.query.billing,
    router.query.reference,
    router.query.trxref,
    router.query.session_id,
    onBillingReady,
    router,
  ]);

  const saveCountry = async () => {
    if (!countryCode || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await screeningClient.setBillingCountry({
        country_code: countryCode,
      });
      const employer = getStoredEmployer();
      if (employer) {
        updateStoredEmployer({
          ...employer,
          country_code: res.country_code,
          billing_region: res.billing_region as BillingRegion,
          billing: res.billing,
        });
      }
      setRegion(res.billing_region as BillingRegion);
      onBillingReady(res.billing, {
        country_code: res.country_code,
        billing_region: res.billing_region as BillingRegion,
      });
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Could not save country.'
      );
    } finally {
      setBusy(false);
    }
  };

  const applyBilling = (
    billing: ScreeningBilling,
    extras?: Partial<{
      country_code: string | null;
      billing_region: BillingRegion | null;
    }>
  ) => {
    const employer = getStoredEmployer();
    if (employer) {
      updateStoredEmployer({
        ...employer,
        country_code: extras?.country_code ?? employer.country_code,
        billing_region: extras?.billing_region ?? employer.billing_region,
        billing,
      });
    }
    onBillingReady(billing, extras);
  };

  const startTrial = async () => {
    if (!catalog || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await screeningClient.billingCheckout({
        plan_id: selected,
        interval,
      });
      if (res.local_trial && res.billing) {
        applyBilling(res.billing, {
          country_code: res.country_code,
          billing_region: res.billing_region as BillingRegion,
        });
        toast.success('Trial started.');
        return;
      }
      if (res.provider === 'stripe' && res.checkout_url) {
        window.location.assign(res.checkout_url);
        return;
      }
      if (res.provider === 'paystack' && res.authorization_url) {
        window.location.assign(res.authorization_url);
        return;
      }
      setError('Checkout could not start. Try again.');
      setBusy(false);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Checkout could not start.'
      );
      setBusy(false);
    }
  };

  const redeemEnterprise = async () => {
    if (!enterpriseCode.trim() || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await screeningClient.redeemEnterprise({
        code: enterpriseCode.trim(),
      });
      applyBilling(res.billing, {
        country_code: res.country_code,
        billing_region: res.billing_region as BillingRegion,
      });
      toast.success('Enterprise access unlocked.');
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'That enterprise code is not valid.'
      );
      setBusy(false);
    }
  };

  if (!region || !catalog) {
    return (
      <Overlay className="screening-root">
        <Shell>
          <Head>
            <Eyebrow>BILLING</Eyebrow>
            <DisplayTitle>Where is the company based?</DisplayTitle>
            <MutedText>
              The country you choose locks the price list. Companies in Africa
              are billed in naira through Paystack. Everyone else is billed in
              US dollars through Stripe.
            </MutedText>
          </Head>
          <CountryBlock>
            <Select
              label="Country"
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              helper={
                countryCode
                  ? billingHintForCountry(countryCode)
                  : 'Select a country before you can start a trial.'
              }
            >
              <option value="" disabled>
                Select country
              </option>
              {COUNTRY_OPTIONS.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </Select>
            {error && <FormError>{error}</FormError>}
            <Button
              size="lg"
              disabled={!countryCode || busy}
              onClick={saveCountry}
            >
              {busy ? 'Saving' : 'Continue'}
            </Button>
          </CountryBlock>
        </Shell>
      </Overlay>
    );
  }

  return (
    <Overlay className="screening-root">
      <Shell>
        <Head>
          <Eyebrow>PLANS</Eyebrow>
          <DisplayTitle>Choose a plan</DisplayTitle>
          <MutedText>
            Companies and agencies use the same three plans. Every plan includes
            the full product. Volume and support change after the free mandate.
          </MutedText>
        </Head>

        <PlanPicker
          catalog={catalog}
          interval={interval}
          selected={selected}
          onInterval={setInterval}
          onSelect={setSelected}
        />

        <StartSection>
          <Head>
            <Eyebrow>START A FREE SCREENING</Eyebrow>
            <SectionLead>Run the first mandate before you pay</SectionLead>
          </Head>
          <StartCopy>
            <BodyText>{trialCopy(catalog)}</BodyText>
            <MutedText>{NEVER_PAY_FOR}</MutedText>
          </StartCopy>
          {error && <FormError>{error}</FormError>}
          <Actions>
            <Button size="lg" disabled={busy} onClick={startTrial}>
              {busy ? 'Starting trial' : 'Start free trial'}
            </Button>
            <MutedText style={{ fontSize: 13 }}>
              We will ask for a card so the selected plan can continue after the
              first role. You will not be charged until you publish a second
              role or use the interview allowance.
            </MutedText>
          </Actions>
        </StartSection>

        <EnterpriseSection>
          <Head>
            <Eyebrow>ENTERPRISE</Eyebrow>
            <SectionLead>Skip these plans if HireMe set you up</SectionLead>
          </Head>
          <StartCopy>
            <BodyText>
              Banks, groups, and other enterprise customers do not pick Starter,
              Growth, or Scale. Enter the code your HireMe contact sent.
            </BodyText>
          </StartCopy>
          <EnterpriseRow>
            <TextField
              label="Enterprise code"
              value={enterpriseCode}
              onChange={(e) => setEnterpriseCode(e.target.value)}
              autoComplete="off"
            />
            <Button
              size="lg"
              disabled={busy || !enterpriseCode.trim()}
              onClick={redeemEnterprise}
            >
              {busy ? 'Unlocking' : 'Unlock enterprise'}
            </Button>
          </EnterpriseRow>
        </EnterpriseSection>
      </Shell>
    </Overlay>
  );
};

export default PlansGate;
